import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { useTranslation } from "./i18n";
import {
  Login,
  UserRegistration,
  OnlineOrder,
  MediaContent,
  PaymentStatus,
  NotFound,
} from "./pages";
import FileManager from "./pages/FileManager";
import TransactionReview from "./pages/TransactionReview";
import AdminLayout from "./features/admin/layout";
import RecentCustomerPage from "./features/admin/pages/RecentCustomerPage";
import RestoreAccessPage from "./features/admin/pages/RestoreAccessPage";
import SuperLayout from "./features/super/layout";
import CustomersPage from "./features/super/pages/CustomersPage";
import EmployeesPage from "./features/super/pages/EmployeesPage";
import PackagesPage from "./features/super/pages/PackagesPage";
import LocationsPage from "./features/super/pages/LocationsPage";
import ServicesPage from "./features/super/pages/ServicesPage";
import AllowedPaperWalletsPage from "./features/super/pages/AllowedPaperWalletsPage";
import HolidaysPage from "./features/super/pages/HolidaysPage";
import MediaContentPage from "./features/super/pages/MediaContentPage";
import FlexibleDiscountPage from "./features/super/pages/FlexibleDiscountPage";
import MarketingPage from "./features/super/pages/MarketingPage";
import ComputersPage from "./features/super/pages/ComputersPage";
import { ThemeProvider } from "@material-tailwind/react";
import mirVrTheme from "./materialTailwindTheme.js";
import PrivateRoute from "./components/PrivateRoute.jsx";
import {
  selectAccessToken,
  setUser,
  setLocation,
  updateAccessToken,
} from "./store/auth";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import * as userApi from "./api/user";
import { backendErrorLoggerFactory } from "./ErrorBoundary";

/**
 * @returns {React.Component}
 */
function App() {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const dispatch = useDispatch();

  window.onerror = backendErrorLoggerFactory();

  useEffect(() => {
    const inner = async () => {
      if (accessToken == null) {
        dispatch(setUser(null));
      } else {
        try {
          const resp = await userApi.me(accessToken);
          if (resp == null) {
            dispatch(updateAccessToken(null));
            return;
          }
          const { user, location } = resp;
          toast.success(t("auth.loginSuccess"));
          dispatch(setUser(user));
          dispatch(setLocation(location));
        } catch (e) {
          console.error(e);
        }
      }
    };
    toast.promise(inner(), {
      loading: t("auth.loggingIn"),
      error: t("auth.fetchUserFailed"),
    });
  }, [accessToken]);

  return (
    <>
      <ThemeProvider value={mirVrTheme}>
        <Toaster
          position="top-center"
          reverseOrder={false}
          gutter={8}
          containerClassName=""
          containerStyle={{}}
          toastOptions={{
            // Define default options
            className: "",
            duration: 5000,
            style: {
              background: "#2A3955",
              color: "#fff",
            },

            // Default options for specific types
            success: {
              duration: 3000,
              theme: {
                primary: "#1FA334",
              },
            },
            error: {
              duration: 3000,
              theme: {
                primary: "#973939",
              },
            },
          }}
        />
        <Routes>
          <Route path="/">
            <Route element={<Navigate replace to="/dashboard/admin" />} index />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<UserRegistration />} />
          <Route path="/order">
            <Route path="" element={<OnlineOrder />} />
            <Route
              path="success"
              element={<PaymentStatus status="success" />}
            />
            <Route
              path="failure"
              element={<PaymentStatus status="failure" />}
            />
          </Route>
          <Route path="/dashboard" element={<PrivateRoute to={[1, 2]} />}>
            <Route path="media" element={<MediaContent />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route
                index
                element={<Navigate to="recent-customer" replace />}
              />
              <Route path="recent-customer" element={<RecentCustomerPage />} />
              <Route path="restore-access" element={<RestoreAccessPage />} />
            </Route>
            <Route element={<PrivateRoute to={[2]} />}>
              <Route path="super" element={<SuperLayout />}>
                <Route index element={<Navigate to="customers" replace />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="packages" element={<PackagesPage />} />
                <Route path="locations" element={<LocationsPage />} />
                <Route path="services" element={<ServicesPage />} />
                <Route
                  path="allowed-paper-wallets"
                  element={<AllowedPaperWalletsPage />}
                />
                <Route path="holidays" element={<HolidaysPage />} />
                <Route path="media-content" element={<MediaContentPage />} />
                <Route
                  path="flexible-discount"
                  element={<FlexibleDiscountPage />}
                />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="computers" element={<ComputersPage />} />
              </Route>
              <Route path="file-manager" element={<FileManager />} />
            </Route>
          </Route>
          <Route
            path="/review/transaction/:id"
            element={<TransactionReview />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ThemeProvider>
    </>
  );
}

export default App;
